import { useEffect, useState } from 'react'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import { isManagerRole } from '@/utils/isManagerRole'
import { logError } from './errorUtils'

/**
 * Checks whether the current user has permission to place orders
 * by looking at their PractitionerRole SNOMED codes.
 * Managers and CC agents cannot order; everyone else can.
 */
export function useCanOrder(): { canOrder: boolean; loading: boolean } {
  const medplum = useMedplum()
  const profile = useMedplumProfile()
  const [canOrder, setCanOrder] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!profile || profile.resourceType !== 'Practitioner') {
      setCanOrder(false)
      setLoading(false)
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        // All roles + system-checked, matching the middleware manager rule (and
        // the other FE gates); a single arbitrary role is non-deterministic.
        const roles = await medplum.searchResources(
          'PractitionerRole',
          `practitioner=Practitioner/${profile.id}`,
        )
        if (cancelled) return

        // No role → allow (backwards compat); managers/CC cannot order.
        setCanOrder(!isManagerRole(roles))
      } catch (err) {
        logError('Role lookup', err)
        // If we can't determine role, default to the old behaviour
        if (!cancelled) setCanOrder(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [medplum, profile])

  return { canOrder, loading }
}
